import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { defineMessages } from 'react-intl-next';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import { fg } from '@confluence/feature-gating';
import {
	useObjectSidebarActions,
	usePanel,
	useIsObjectSidebarShown,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { PageMode, ContentStatus } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus.ts';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	useCommentsPanelActions,
	type ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { usePageContentId } from '@confluence/page-context';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import {
	isSpaceOverview,
	isDatabase,
	isWhiteboard,
} from '@confluence/named-routes/entry-points/routes-utils.ts';
import { SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/comments-data-manager/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import type { PanelProps } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';

import { useUnreadPanelComments } from './hooks/useUnreadPanelComments.ts';
import type { CommentsPanelWrapperProps } from './CommentsPanelWrapper.tsx';

const CommentsPanelFilterMenu = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-useShowCommentsPanel" */ './CommentsPanelFilterMenu.tsx'
			)
		).CommentsPanelFilterMenu,
});

const CommentsPanelFooter = LoadableLazy<PanelProps>({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-useShowCommentsPanel" */ './CommentsPanelFooter.tsx'))
			.CommentsPanelFooter,
});

const CommentsPanelHeaderIcon = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-useShowCommentsPanel" */ './CommentsPanelHeaderIcon.tsx'
			)
		).CommentsPanelHeaderIcon,
});

const CommentsPanelWrapper = LoadableLazy<CommentsPanelWrapperProps>({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-useShowCommentsPanel" */ './CommentsPanelWrapper.tsx'))
			.CommentsPanelWrapper,
});

export const i18n = defineMessages({
	commentsPanelHeaderLabel: {
		id: 'comments-panel.panel.header.label',
		defaultMessage: 'Comments',
		description: 'Label for the comments panel header',
	},
});

export const useShowCommentsPanel = () => {
	const isSpaceOverviewPage = isSpaceOverview();
	const isDatabaseView = isDatabase();
	const isWhiteboardView = isWhiteboard();
	const { handleMarkCommentsAsRead } = useUnreadPanelComments();
	const { contentStatus } = useContentStatus();
	const [shouldClearUnreads, setShouldClearUnreads] = useState<boolean>(false);

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { showObjectSidebar, hideObjectSidebar } = useObjectSidebarActions();

	const { setViewProps, setEditorViewProps } = useAnnotationsDispatch();

	const { setCurrentView, setCurrentlySelectedThreadKey, setReplyToCommentId } =
		useCommentsPanelActions();
	const [contentId] = usePageContentId();
	const { canAddComments } = useAddCommentPermissionCheck(contentId ?? '', true);

	const pageMode = useGetPageMode();
	const eventEmitter =
		pageMode === PageMode.VIEW
			? getRendererAnnotationEventEmitter()
			: getEditorAnnotationEventEmitter();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { showModal } = useDialogs();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();

	const allowComments = useEmbeddedComments();

	// a ref is needed here because of a closure issue with canClosePanel
	const hasContentChangedRef = useRef(hasContentChanged);
	useEffect(() => {
		hasContentChangedRef.current = hasContentChanged;
	}, [hasContentChanged]);

	// Required to invoke handleMarkCommentsAsRead and if any commentsMarkedAsRead
	// then we will call the MarkCommentsReadMutation Mutation
	useEffect(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (shouldClearUnreads) {
			handleMarkCommentsAsRead();
		}
		return () => {
			setShouldClearUnreads(false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldClearUnreads, setShouldClearUnreads]);

	const onPanelCloseFunction = () => {
		setCurrentView(undefined);
		setShouldClearUnreads(true);

		// Unselect active annotations on the page
		if (pageMode === PageMode.VIEW) {
			eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
			eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
			setViewProps(null);
		} else {
			eventEmitter.emit('setselectedannotation', '');
			setEditorViewProps(null);
		}

		// clear selected comment thread in the comments panel
		setCurrentlySelectedThreadKey('');
		if (expValEquals('confluence_comments_5766_bugfix', 'isEnabled', true)) {
			setReplyToCommentId();
		}
	};

	const onPanelCloseCallback = useCallback(onPanelCloseFunction, [
		setCurrentView,
		setShouldClearUnreads,
		pageMode,
		eventEmitter,
		setViewProps,
		setEditorViewProps,
		setCurrentlySelectedThreadKey,
		setReplyToCommentId,
	]);

	const onPanelClose = expValEquals(
		'confluence_edit_page_rerender_optimizations',
		'isEnabled',
		true,
	)
		? onPanelCloseCallback
		: onPanelCloseFunction;

	const canClosePanel = useCallback(async () => {
		if (hasContentChangedRef.current && !fg('confluence_comments_fix_duplicate_modal')) {
			return new Promise<boolean>((resolve) => {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged && resetContentChanged();
						resolve(true);
					},
					onCancel: () => {
						resolve(false);
					},
				});
			});
		} else {
			return true;
		}
	}, [resetContentChanged, showModal]);

	const showCommentsPanelFunction = ({ openWithView }: { openWithView?: ViewValues } = {}) => {
		if (!allowComments) {
			return;
		}

		experienceTracker.start({
			name: SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE,
			attributes: {
				...getCommentsDataManagerExperimentAttributes(),
				openWithView,
				contentId,
				pageMode,
			},
		});

		const loadFooterComments = fg('confluence_comment_footercomments_editpage')
			? pageMode !== PageMode.EDIT
			: true;

		showObjectSidebar(
			{
				id: PanelName.CommentsPanel,
				headerComponentElements: {
					HeaderIcon: fg('confluence_frontend_comments_panel_v2')
						? CommentsPanelHeaderIcon
						: undefined,
					headerLabel: i18n.commentsPanelHeaderLabel,
					HeaderRightAlignedElement: fg('confluence_frontend_comments_panel_v2')
						? CommentsPanelFilterMenu
						: undefined,
				},
				BodyComponent: () => CommentsPanelWrapper({ openWithView }),
				FooterComponent:
					canAddComments &&
					contentStatus !== ContentStatus.DRAFT &&
					loadFooterComments &&
					!isSpaceOverviewPage &&
					!isDatabaseView &&
					!isWhiteboardView &&
					fg('confluence_frontend_comments_panel_v2')
						? CommentsPanelFooter
						: undefined,
				closeOptions: {
					canClosePanel,
					onPanelClose,
				},
			},
			'push',
		);
	};

	const showCommentsPanelHandler = useCallback(showCommentsPanelFunction, [
		allowComments,
		canAddComments,
		canClosePanel,
		contentId,
		contentStatus,
		experienceTracker,
		isDatabaseView,
		isSpaceOverviewPage,
		isWhiteboardView,
		onPanelClose,
		pageMode,
		showObjectSidebar,
	]);

	const showCommentsPanel = expValEquals(
		'confluence_edit_page_rerender_optimizations',
		'isEnabled',
		true,
	)
		? showCommentsPanelHandler
		: showCommentsPanelFunction;

	// NOTE: Exposing the hide function for now while we have the CommentButton
	return {
		isCommentsPanelShown: isObjectSidebarShown && panel?.id === PanelName.CommentsPanel,
		showCommentsPanel,
		hideCommentsPanel: hideObjectSidebar,
	};
};
